import {
  Box,
  Stack,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { NwButton } from "@nawy/ui";
import { Mention, MentionsInput } from "react-mentions";
import { useCallback, useRef } from "react";
import { ArrowUp } from "lucide-react";
import { getMentionStyle } from "@/modules/lead-page/new/constants";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import useAddComments from "../../hooks/comments/add-comment.hook";
import useSanitizeMentions from "@/modules/lead-page/new/hooks/sanitize-mentions.hook";
import MentionSuggestion from "@/modules/lead-page/new/components/activity/mention-suggestion";
import { TrackCommentSource } from "../../dtos";
import ReadonlyWrapper from "@/modules/common/components/wrappers/readonly-wrapper";

interface AddTrackCommentProps {
  trackUId: string;
  lastActivityCardStyle?: boolean;
  source?: TrackCommentSource;
}

const AddTrackComment = ({
  trackUId,
  source,
  lastActivityCardStyle = false,
}: AddTrackCommentProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    commentMessage,
    mentionedUsers,
    loadAddingComment,
    notifySalesAgent,
    setNotifySalesAgent,
    handleAddComments,
    handleTextareaChange,
    handleKeyPress,
  } = useAddComments({ trackUId, source });

  const showNotifyOption = source !== TrackCommentSource.construction;

  const isButtonDisabled = loadAddingComment || !commentMessage.trim();

  /* ---------------- Input ---------------- */
  const sanitizedMentionData = useSanitizeMentions({ mentionedUsers });

  const renderMentionSuggestion = useCallback(
    (
      suggestion: { id: string; display: string },
      _search: string,
      _highlightedDisplay: React.ReactNode,
      _index: number,
      focused: boolean,
    ) => {
      return (
        <MentionSuggestion
          mentionedUsers={mentionedUsers}
          suggestion={suggestion}
          focused={focused}
        />
      );
    },
    [mentionedUsers],
  );

  const renderCommentInput = useCallback(() => {
    return (
      <Box sx={{ flex: 1, overflow: "visible" }}>
        <MentionsInput
          inputRef={inputRef}
          value={commentMessage}
          onChange={handleTextareaChange}
          onKeyPress={handleKeyPress}
          placeholder={isMobile ? "Comment" : "Add new comment"}
          style={getMentionStyle()}
        >
          <Mention
            trigger="@"
            data={sanitizedMentionData}
            displayTransform={(_, display) => `@${display}`}
            renderSuggestion={renderMentionSuggestion}
            style={{
              backgroundColor: "#e5f3ff",
              fontWeight: 500,
              borderRadius: 4,
              padding: `0 ${theme.nawy.spacing.xs}`,
            }}
          />
        </MentionsInput>
      </Box>
    );
  }, [
    commentMessage,
    handleTextareaChange,
    handleKeyPress,
    isMobile,
    renderMentionSuggestion,
    sanitizedMentionData,
    theme,
  ]);

  /* ---------------- Button ---------------- */

  const renderAddButton = useCallback(() => {
    if (lastActivityCardStyle) {
      return (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: theme.nawy.colors.semantic.layer.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: isButtonDisabled ? 0.5 : 1,
            pointerEvents: isButtonDisabled ? "none" : "auto",
            cursor: isButtonDisabled ? "not-allowed" : "pointer",
          }}
          onClick={isButtonDisabled ? undefined : handleAddComments}
        >
          {loadAddingComment ? (
            <CircularProgress size={20} />
          ) : (
            <ArrowUp
              size={16}
              color={theme.nawy.colors.semantic.text.onColor}
              strokeWidth={2}
            />
          )}
        </Box>
      );
    }

    return (
      <NwButton
        onClick={handleAddComments}
        disabled={isButtonDisabled}
        sx={{ whiteSpace: "nowrap", minWidth: "120px" }}
      >
        {loadAddingComment ? <CircularProgress size={20} /> : "Add Comment"}
      </NwButton>
    );
  }, [
    lastActivityCardStyle,
    isButtonDisabled,
    loadAddingComment,
    handleAddComments,
    theme,
  ]);

  /* ---------------- Layout ---------------- */

  return (
    <ReadonlyWrapper>
      <Stack
        direction="row"
        alignItems="center"
        spacing={theme.nawy.spacing.xs}
        borderTop={lastActivityCardStyle ? "none" : "1px solid #DEE2E7"}
        sx={{
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.xs,
          position: "sticky",
          bottom: 0,
          background: theme.nawy.colors.semantic.layer.primary,
          zIndex: 1000,
          border: lastActivityCardStyle
            ? `1px solid ${theme.nawy.colors.semantic.border.primary}`
            : "none",
          borderRadius: isMobile ? theme.nawy.radius.xl : 0,
        }}
      >
        {renderCommentInput()}

        {renderAddButton()}

        {/* Desktop-only notify */}
        {!isMobile && showNotifyOption && (
          <FormControlLabel
            control={
              <Checkbox
                checked={notifySalesAgent}
                onChange={(e) => setNotifySalesAgent(e.target.checked)}
                size="medium"
              />
            }
            label="Notify Sales Agent"
            sx={{
              whiteSpace: "nowrap",
              "& .MuiFormControlLabel-label": {
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              },
            }}
          />
        )}
      </Stack>

      {/* Mobile-only notify */}
      {isMobile && showNotifyOption && (
        <FormControlLabel
          control={
            <Checkbox
              checked={notifySalesAgent}
              onChange={(e) => setNotifySalesAgent(e.target.checked)}
              size="small"
            />
          }
          label="Notify Sales Agent"
          sx={{
            mt: "2px",
            mb: "4px",
            "& .MuiFormControlLabel-label": {
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.secondary,
            },
          }}
        />
      )}
    </ReadonlyWrapper>
  );
};

export default AddTrackComment;
